import React, { useState } from 'react';
import { Table } from './Table';
import eyeIcon from '../assets/eye.png';
import forwardIcon from '../assets/forward.png';
import inverseIcon from '../assets/inverse.png';
import { ParameterDiagramModal } from './ParameterDiagramModal';
import { SAIFileResponse, trainedModelsApi } from '../services/api';

interface Column {
  key: string;
  header: string;
  render?: (item: any) => React.ReactNode;
  cellClassName?: string;
}

type ModelMode = 'prediction' | 'design';

interface Parameter {
  id: string;
  name: string; // Backend reference name
  label: string; // Display label
  icon: string;
  value: number;
  validRange: string;
  unit: string;
  optimize?: boolean;
}

interface ExistingModelParametersProps {
  selectedSAIFile: SAIFileResponse;
  modelName: string;
  initialMode?: ModelMode;
  /** Saved parameter values from the last prediction run; used when returning via "Get New Prediction" */
  initialPredictionParameters?: any[] | null;
  /** Saved design form state; used when returning via "Run New Analysis" */
  initialDesignState?: {
    designValues: Record<string, number | null>;
    optimizeFlags: Record<string, boolean>;
    constraintTypes: Record<string, string>;
    constraintValues: Record<string, string>;
  } | null;
  onBack: () => void;
  onNext: (mode: ModelMode, paramResult?: any[], designState?: {
    designValues: Record<string, number | null>;
    optimizeFlags: Record<string, boolean>;
    constraintTypes: Record<string, string>;
    constraintValues: Record<string, string>;
  }, inputValues?: number[]) => void;
  onVisualize?: () => void;
  onOperationInProgress?: (inProgress: boolean) => void;
}

export const ExistingModelParameters: React.FC<ExistingModelParametersProps> = ({
  selectedSAIFile,
  modelName,
  initialMode = 'prediction',
  initialPredictionParameters,
  initialDesignState,
  onBack,
  onNext,
  onVisualize,
  onOperationInProgress,
}) => {
  const [selectedMode, setSelectedMode] = useState<ModelMode>(initialMode);
  const [editingParameterId, setEditingParameterId] = useState<string | null>(null);
  const [editingConstraintId, setEditingConstraintId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDiagramModalOpen, setIsDiagramModalOpen] = useState(false);

  // Notify parent when operation is in progress
  React.useEffect(() => {
    onOperationInProgress?.(isLoading);
    return () => onOperationInProgress?.(false);
  }, [isLoading, onOperationInProgress]);

  const diagramImageUrl = selectedSAIFile?.image ?? null;

  // Base parameters structure from the selected SAI file (computed once)
  const baseParameters = React.useMemo(() => {
    const mappedParams = (selectedSAIFile.parameters || []).map(param => ({
      id: param.id?.toString() || '',
      name: param.name || '', // Backend reference name
      label: param.label || '', // Display label
      icon: param.icon || '',
      validRange: `${param.min} - ${param.max}`,
      unit: param.unit || '',
      defaultValue: param.default || 0,
      min: param.min || 0,
      max: param.max || 0,
    }));

    // Sort to place output parameters first (they will be optimized/predicted)
    return mappedParams.sort((a, b) => {
      const aParam = (selectedSAIFile.parameters || []).find(p => p.id?.toString() === a.id);
      const bParam = (selectedSAIFile.parameters || []).find(p => p.id?.toString() === b.id);
      const aType = (aParam?.type || '').toLowerCase();
      const bType = (bParam?.type || '').toLowerCase();
      if (aType === 'output' && bType !== 'output') return -1;
      if (bType === 'output' && aType !== 'output') return 1;
      return 0;
    });
  }, [selectedSAIFile]);

  console.log(baseParameters);

  // Separate state for prediction mode values (seed from saved params when returning via "Get New Prediction")
  const [predictionValues, setPredictionValues] = useState<Record<string, number>>(() => {
    const values: Record<string, number> = {};
    const saved = initialPredictionParameters && initialPredictionParameters.length > 0
      ? Object.fromEntries(
          initialPredictionParameters
            .filter((p: any) => p.id != null)
            .map((p: any) => {
              const id = p.id?.toString() || '';
              const num = Number(p.value);
              return [id, Number.isNaN(num) ? (p.default ?? 0) : num] as const;
            })
        )
      : null;
    (selectedSAIFile.parameters || []).forEach(param => {
      const id = param.id?.toString() || '';
      values[id] = saved && id in saved ? saved[id] : (param.default || 0);
    });
    return values;
  });

  // Separate state for design mode values (seed from saved state when returning via "Run New Analysis")
  const [designValues, setDesignValues] = useState<Record<string, number | null>>(() => {
    const values: Record<string, number | null> = {};
    const saved = initialDesignState?.designValues;
    (selectedSAIFile.parameters || []).forEach(param => {
      const id = param.id?.toString() || '';
      if (saved && id in saved && saved[id] !== undefined) {
        values[id] = saved[id];
      } else {
        const paramType = (param.type || '').toLowerCase();
        values[id] = paramType === 'output' ? null : (param.min || 0);
      }
    });
    return values;
  });

  // State for optimize checkboxes (seed from saved state when returning)
  const [optimizeFlags, setOptimizeFlags] = useState<Record<string, boolean>>(() => {
    const flags: Record<string, boolean> = {};
    const saved = initialDesignState?.optimizeFlags;
    (selectedSAIFile.parameters || []).forEach(param => {
      const id = param.id?.toString() || '';
      const paramType = (param.type || '').toLowerCase();
      flags[id] = saved && id in saved ? saved[id] : paramType === 'output';
    });
    return flags;
  });

  // State for constraint types (seed from saved state when returning)
  const [constraintTypes, setConstraintTypes] = useState<Record<string, string>>(() => {
    const types: Record<string, string> = {};
    const saved = initialDesignState?.constraintTypes;
    (selectedSAIFile.parameters || []).forEach(param => {
      const id = param.id?.toString() || '';
      const paramType = (param.type || '').toLowerCase();
      types[id] = saved && id in saved ? saved[id] : (paramType === 'output' ? 'between-range' : 'maximum-value');
    });
    return types;
  });

  // State for constraint values (seed from saved state when returning)
  const [constraintValues, setConstraintValues] = useState<Record<string, string>>(() => {
    const values: Record<string, string> = {};
    const saved = initialDesignState?.constraintValues;
    (selectedSAIFile.parameters || []).forEach(param => {
      const id = param.id?.toString() || '';
      if (saved && id in saved) {
        values[id] = saved[id];
      } else {
        const paramType = (param.type || '').toLowerCase();
        const isOutput = paramType === 'output';
        values[id] = isOutput
          ? `${param.min || 0} - ${param.max || 0}`
          : (param.max?.toString() || '0.01');
      }
    });
    return values;
  });

  // Combine base parameters with the appropriate values based on mode
  const parameters: Parameter[] = baseParameters
    .filter(param => {
      // In prediction mode, only show input parameters (outputs will be predicted)
      if (selectedMode === 'prediction') {
        const matchingParam = (selectedSAIFile.parameters || []).find(p => p.id?.toString() === param.id);
        const paramType = (matchingParam?.type || '').toLowerCase();
        return paramType === 'input';
      }
      return true;
    })
    .map(param => ({
      ...param,
      value: selectedMode === 'prediction'
        ? predictionValues[param.id]
        : (designValues[param.id] ?? 0), // Use 0 as fallback for type safety
      optimize: optimizeFlags[param.id],
    }));

  // Helper functions to parse and format range values
  const parseRangeValue = (value: string): { min: string; max: string } => {
    const parts = value.split('-').map(s => s.trim()).filter(s => s !== '');
    return {
      min: parts[0] || '',
      max: parts[1] || '',
    };
  };

  const formatRangeValue = (min: string, max: string): string => {
    return `${min} - ${max}`;
  };

  // Parameter IDs whose value is outside valid range (min/max) — block submit and highlight red
  const invalidValueParamIds = React.useMemo(() => {
    const set = new Set<string>();
    if (selectedMode === 'prediction') {
      baseParameters
        .filter(p => {
          const matchingParam = (selectedSAIFile.parameters || []).find(param => param.id?.toString() === p.id);
          const paramType = (matchingParam?.type || '').toLowerCase();
          return paramType === 'input';
        })
        .forEach(p => {
          const v = predictionValues[p.id];
          const min = p.min ?? 0, max = p.max ?? 0;
          if (v == null || Number.isNaN(v) || v < min || v > max) set.add(p.id);
        });
    } else {
      baseParameters.forEach(p => {
        if (optimizeFlags[p.id]) return;
        const matchingParam = (selectedSAIFile.parameters || []).find(param => param.id?.toString() === p.id);
        const paramType = (matchingParam?.type || '').toLowerCase();
        if (paramType === 'output') return; // Skip output parameters in design mode validation
        const v = designValues[p.id];
        const min = p.min ?? 0, max = p.max ?? 0;
        if (v == null || Number.isNaN(Number(v)) || v < min || v > max) set.add(p.id);
      });
    }
    return set;
  }, [selectedMode, baseParameters, predictionValues, designValues, optimizeFlags, selectedSAIFile.parameters]);

  // Parameter IDs whose constraint values are outside valid range — block submit and highlight red
  const invalidConstraintParamIds = React.useMemo(() => {
    const set = new Set<string>();
    if (selectedMode === 'design') {
      baseParameters.forEach(p => {
        if (!optimizeFlags[p.id]) return; // Only validate optimized parameters
        
        const constraintType = constraintTypes[p.id];
        const constraintValue = constraintValues[p.id];
        const min = p.min ?? 0, max = p.max ?? 0;

        if (constraintType === 'between-range') {
          // Validate range: both min and max should be within valid range
          const { min: rangeMin, max: rangeMax } = parseRangeValue(constraintValue);
          const rangeMinNum = parseFloat(rangeMin);
          const rangeMaxNum = parseFloat(rangeMax);
          
          if (
            Number.isNaN(rangeMinNum) || Number.isNaN(rangeMaxNum) ||
            rangeMinNum < min || rangeMinNum > max ||
            rangeMaxNum < min || rangeMaxNum > max ||
            rangeMinNum >= rangeMaxNum
          ) {
            set.add(p.id);
          }
        } else if (['maximum-value', 'minimum-value', 'greater-than', 'less-than', 'equal-to'].includes(constraintType)) {
          // Validate single value: should be within valid range
          const value = parseFloat(constraintValue);
          if (Number.isNaN(value) || value < min || value > max) {
            set.add(p.id);
          }
        }
      });
    }
    return set;
  }, [selectedMode, baseParameters, optimizeFlags, constraintTypes, constraintValues]);

  const hasInvalidValues = invalidValueParamIds.size > 0 || invalidConstraintParamIds.size > 0;

  const handleEditClick = (parameterId: string) => {
    if (!isLoading) {
      setEditingParameterId(parameterId);
    }
  };

  const handleParameterChange = (parameterId: string, newValue: number | null) => {
    if (selectedMode === 'prediction') {
      if (newValue !== null) {
        setPredictionValues(prev => ({
          ...prev,
          [parameterId]: newValue,
        }));
      }
    } else {
      setDesignValues(prev => ({
        ...prev,
        [parameterId]: newValue,
      }));
    }
  };

  const handleOptimizeToggle = (parameterId: string) => {
    if (!isLoading) {
      setOptimizeFlags(prev => ({
        ...prev,
        [parameterId]: !prev[parameterId],
      }));
    }
  };

  const handleConstraintTypeChange = (parameterId: string, constraintType: string) => {
    setConstraintTypes(prev => ({
      ...prev,
      [parameterId]: constraintType,
    }));

    const param = (selectedSAIFile.parameters || []).find(p => p.id?.toString() === parameterId);
    
    // Set constraint value based on constraint type
    if (constraintType === 'between-range') {
      // Set the constraint value to the valid range
      if (param) {
        setConstraintValues(prev => ({
          ...prev,
          [parameterId]: `${param.min || 0} - ${param.max || 0}`,
        }));
      }
    } else if (constraintType === 'maximum-value') {
      // Set to the max value from valid range
      if (param) {
        setConstraintValues(prev => ({
          ...prev,
          [parameterId]: (param.max || 0).toString(),
        }));
      }
    } else if (constraintType === 'minimum-value') {
      // Set to the min value from valid range
      if (param) {
        setConstraintValues(prev => ({
          ...prev,
          [parameterId]: (param.min || 0).toString(),
        }));
      }
    } else if (constraintType === 'greater-than') {
      // Set to the min value from valid range
      if (param) {
        setConstraintValues(prev => ({
          ...prev,
          [parameterId]: (param.min || 0).toString(),
        }));
      }
    } else if (constraintType === 'less-than') {
      // Set to the max value from valid range
      if (param) {
        setConstraintValues(prev => ({
          ...prev,
          [parameterId]: (param.max || 0).toString(),
        }));
      }
    } else if (constraintType === 'equal-to') {
      // Set to the midpoint of the valid range
      if (param) {
        const midpoint = ((param.min || 0) + (param.max || 0)) / 2;
        setConstraintValues(prev => ({
          ...prev,
          [parameterId]: midpoint.toString(),
        }));
      }
    }

    // If switching to maximum-value or minimum-value, close editing
    if (['maximum-value', 'minimum-value'].includes(constraintType)) {
      setEditingConstraintId(null);
    }
  };

  const handleConstraintValueChange = (parameterId: string, value: string) => {
    setConstraintValues(prev => ({
      ...prev,
      [parameterId]: value,
    }));
  };

  const handleRangeValueChange = (parameterId: string, min: string, max: string) => {
    const formattedValue = formatRangeValue(min, max);
    setConstraintValues(prev => ({
      ...prev,
      [parameterId]: formattedValue,
    }));
  };

  const handleGetPrediction = async () => {
    if (selectedMode !== 'prediction') {
      onNext(selectedMode);
      return;
    }
    if (hasInvalidValues) return;

    // Find the trained model by name
    const trainedModel = selectedSAIFile.trained_models?.find(
      model => model.name === modelName
    );

    if (!trainedModel || trainedModel.id == null) {
      setError('Model not found');
      return;
    }

    // Build input array in the correct order (following parameter order from SAI file)
    // Exclude output parameters and entrainment ratio for prediction
    const inputArray: number[] = [];

    // Get parameters in their original order from the SAI file
    // Filter to only include input parameters (type === 'input')
    (selectedSAIFile.parameters || []).forEach(param => {
      const paramType = (param.type || '').toLowerCase();
      if (paramType === 'input') {
        const paramId = param.id?.toString() || '';
        const value = predictionValues[paramId] ?? param.default ?? 0;
        inputArray.push(value);
      }
    });

    setIsLoading(true);
    setError(null);

    try {
      const result = await trainedModelsApi.predict(trainedModel.id, inputArray);
      const paramResult = selectedSAIFile.parameters?.map(param => {
        const paramId = param.id?.toString() || '';
        const paramType = (param.type || '').toLowerCase();
        const isOutputParameter = paramType === 'output';

        return {
          ...param,
          value: isOutputParameter
            ? (result.prediction ?? param.default ?? 0)
            : (predictionValues[paramId] ?? param.default ?? 0),
        };
      });

      // Navigate to results - pass paramResult with updated values and input array
      onNext(selectedMode, paramResult, undefined, inputArray);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to get prediction');
    } finally {
      setIsLoading(false);
    }
  };

  const formatConstraintCondition = (constraintType: string, constraintValue: string): string => {
    switch (constraintType) {
      case 'maximum-value':
        return `less than ${constraintValue}`;
      case 'minimum-value':
        return `greater than ${constraintValue}`;
      case 'between-range':
        return `between ${constraintValue}`;
      case 'greater-than':
        return `greater than ${constraintValue}`;
      case 'less-than':
        return `less than ${constraintValue}`;
      case 'equal-to':
        return `equal to ${constraintValue}`;
      default:
        return constraintValue;
    }
  };

  const handleGetDesign = async () => {
    if (selectedMode !== 'design') {
      onNext(selectedMode);
      return;
    }
    if (hasInvalidValues) return;

    // Find the trained model by name
    const trainedModel = selectedSAIFile.trained_models?.find(
      model => model.name === modelName
    );

    if (!trainedModel || trainedModel.id == null) {
      setError('Model not found');
      return;
    }

    // Build conditions and fixed_values dictionaries
    const conditions: Record<string, string> = {};
    const fixedValues: Record<string, number> = {};

    (selectedSAIFile.parameters || []).forEach(param => {
      const paramId = param.id?.toString() || '';
      const paramName = param.name || '';
      const isOptimized = optimizeFlags[paramId];

      if (isOptimized) {
        // Parameter is optimized - add to conditions
        const constraintType = constraintTypes[paramId] || 'maximum-value';
        const constraintValue = constraintValues[paramId] || '';
        if (paramName && constraintValue) {
          conditions[paramName] = formatConstraintCondition(constraintType, constraintValue);
        }
      } else {
        // Parameter is fixed - add to fixed_values
        const value = designValues[paramId];
        if (value !== null && value !== undefined && paramName) {
          fixedValues[paramName] = value;
        }
      }
    });

    setIsLoading(true);
    setError(null);

    try {
      const result = await trainedModelsApi.design(trainedModel.id, conditions, fixedValues);
      // Navigate to results - pass the design result and form state for "Run New Analysis"
      onNext(selectedMode, result, {
        designValues: { ...designValues },
        optimizeFlags: { ...optimizeFlags },
        constraintTypes: { ...constraintTypes },
        constraintValues: { ...constraintValues },
      });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to get design');
    } finally {
      setIsLoading(false);
    }
  };

  const predictionColumns: Column[] = [
    {
      key: 'name',
      header: 'Parameter Name',
      cellClassName: 'non-editable-column',
      render: (item: any) => (
        <div className="parameter-name-cell">
          <span className="parameter-icon">{item.icon}</span>
          <span>{item.label}</span>
        </div>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      cellClassName: 'editable-column',
      render: (item: any) => {
        const hasValue = item.value != null && !Number.isNaN(item.value);
        const displayValue = hasValue ? item.value : '-';
        const isInvalid = invalidValueParamIds.has(item.id);
        return (
        <div className={`parameter-value-cell ${isInvalid ? 'parameter-value-invalid' : ''}`}>
          {editingParameterId === item.id && !isLoading ? (
            <input
              type="number"
              value={hasValue ? item.value : ''}
              onChange={(e) => handleParameterChange(item.id, parseFloat(e.target.value))}
              onBlur={() => setEditingParameterId(null)}
              autoFocus
              className={`parameter-input ${isInvalid ? 'parameter-input-invalid' : ''}`}
            />
          ) : (
            <span
              onClick={() => handleEditClick(item.id)}
              style={{
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {displayValue}
              {!isLoading && (
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, alignSelf: 'center' }}>
                  <g clipPath="url(#clip0_440_1577)">
                    <path d="M1.43802 9.05782C1.46099 8.85107 1.47248 8.7477 1.50376 8.65108C1.53151 8.56535 1.57072 8.48378 1.62032 8.40856C1.67623 8.32377 1.74977 8.25023 1.89686 8.10314L8.50001 1.5C9.0523 0.947714 9.94773 0.947714 10.5 1.5C11.0523 2.05228 11.0523 2.94771 10.5 3.5L3.89686 10.1031C3.74977 10.2502 3.67623 10.3238 3.59144 10.3797C3.51622 10.4293 3.43465 10.4685 3.34892 10.4962C3.2523 10.5275 3.14893 10.539 2.94218 10.562L1.25 10.75L1.43802 9.05782Z" stroke="#103097" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_440_1577">
                      <rect width="12" height="12" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              )}
            </span>
          )}
        </div>
        );
      },
    },
    {
      key: 'validRange',
      header: 'Valid Range',
      cellClassName: 'non-editable-column',
    },
    {
      key: 'unit',
      header: 'Unit',
      cellClassName: 'non-editable-column',
    },
  ];

  const designColumns: Column[] = [
    {
      key: 'optimize',
      header: 'Optimize',
      cellClassName: 'editable-column',
      render: (item: any) => {
        const matchingParam = (selectedSAIFile.parameters || []).find(p => p.id?.toString() === item.id);
        const paramType = (matchingParam?.type || '').toLowerCase();
        const isOutputParameter = paramType === 'output';
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <input
              type="checkbox"
              checked={item.optimize || false}
              onChange={() => handleOptimizeToggle(item.id)}
              disabled={isOutputParameter || isLoading}
              className="optimize-checkbox"
              style={{ cursor: (isOutputParameter || isLoading) ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}
            />
          </div>
        );
      },
    },
    {
      key: 'name',
      header: 'Parameter Name',
      cellClassName: 'non-editable-column',
      render: (item: any) => (
        <div className="parameter-name-cell">
          <span className="parameter-icon">{item.icon}</span>
          <span>{item.label}</span>
        </div>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      cellClassName: 'editable-column',
      render: (item: any) => {
        const designValue = designValues[item.id];
        const isOptimized = item.optimize || false;
        const matchingParam = (selectedSAIFile.parameters || []).find(p => p.id?.toString() === item.id);
        const paramType = (matchingParam?.type || '').toLowerCase();
        const isOutputParameter = paramType === 'output';
        // When optimize is checked, show "-" and do not allow editing
        const displayValue = isOptimized ? '-' : (designValue === null ? '-' : designValue);
        const isEditable = !isOutputParameter && !isOptimized;
        const isInvalid = !isOptimized && invalidValueParamIds.has(item.id);

        return (
          <div className={`parameter-value-cell ${isInvalid ? 'parameter-value-invalid' : ''}`}>
            {!isEditable ? (
              <span>{displayValue}</span>
            ) : editingParameterId === item.id && !isLoading ? (
              <input
                type="number"
                value={designValue === null ? '' : designValue}
                onChange={(e) => {
                  const v = e.target.value;
                  handleParameterChange(item.id, v === '' || isNaN(parseFloat(v)) ? null : parseFloat(v));
                }}
                onBlur={() => setEditingParameterId(null)}
                autoFocus
                className={`parameter-input ${isInvalid ? 'parameter-input-invalid' : ''}`}
              />
            ) : (
              <span
                onClick={() => handleEditClick(item.id)}
                style={{
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                {displayValue}
                {!isLoading && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, alignSelf: 'center' }}>
                    <g clipPath="url(#clip0_440_1577)">
                      <path d="M1.43802 9.05782C1.46099 8.85107 1.47248 8.7477 1.50376 8.65108C1.53151 8.56535 1.57072 8.48378 1.62032 8.40856C1.67623 8.32377 1.74977 8.25023 1.89686 8.10314L8.50001 1.5C9.0523 0.947714 9.94773 0.947714 10.5 1.5C11.0523 2.05228 11.0523 2.94771 10.5 3.5L3.89686 10.1031C3.74977 10.2502 3.67623 10.3238 3.59144 10.3797C3.51622 10.4293 3.43465 10.4685 3.34892 10.4962C3.2523 10.5275 3.14893 10.539 2.94218 10.562L1.25 10.75L1.43802 9.05782Z" stroke="#103097" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_440_1577">
                        <rect width="12" height="12" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                )}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'constraint',
      header: 'Constraint',
      render: (item: any) => {
        const isEditable = item.optimize;
        const constraintType = constraintTypes[item.id] || 'maximum-value';
        const constraintValue = constraintValues[item.id] || '';
        const needsValueInput = !['maximum-value', 'minimum-value'].includes(constraintType);
        const isEditing = editingConstraintId === item.id && isEditable;
        const isInvalid = invalidConstraintParamIds.has(item.id);

        // Get display text based on constraint type
        const getConstraintDisplayText = () => {
          const typeLabels: Record<string, string> = {
            'maximum-value': 'Maximum Value',
            'minimum-value': 'Minimum Value',
            'between-range': 'Between',
            'greater-than': 'Greater than',
            'less-than': 'Less than',
            'equal-to': 'Equal to',
          };
          const typeLabel = typeLabels[constraintType] || constraintType;

          if (!needsValueInput || constraintValue) {
            return `${typeLabel}: ${constraintValue}`;
          }
          return typeLabel;
        };

        return (
          <div className={`constraint-cell ${isInvalid ? 'parameter-value-invalid' : ''}`} style={{ color: isEditable ? 'var(--primaryText)' : 'var(--gray500)', fontWeight: isEditable ? 500 : 'normal' }}>
            {isEditing ? (
              <div
                style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                onBlur={(e) => {
                  // Only close if clicking outside the entire constraint cell
                  const currentTarget = e.currentTarget;
                  // Use setTimeout to allow the relatedTarget to be set
                  setTimeout(() => {
                    if (!currentTarget.contains(document.activeElement)) {
                      setEditingConstraintId(null);
                    }
                  }, 0);
                }}
              >
                <select
                  value={constraintType}
                  onChange={(e) => handleConstraintTypeChange(item.id, e.target.value)}
                  className="constraint-select"
                >
                  <option value="maximum-value">Maximum Value</option>
                  <option value="minimum-value">Minimum Value</option>
                  <option value="between-range">Between a range</option>
                  <option value="greater-than">Greater than</option>
                  <option value="less-than">Less than</option>
                  <option value="equal-to">Equal to</option>
                </select>

                {needsValueInput && (
                  constraintType === 'between-range' ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="number"
                        value={parseRangeValue(constraintValue).min}
                        onChange={(e) => {
                          const { max } = parseRangeValue(constraintValue);
                          handleRangeValueChange(item.id, e.target.value, max);
                        }}
                        placeholder="Min"
                        autoFocus={needsValueInput}
                        className={`constraint-input ${isInvalid ? 'parameter-input-invalid' : ''}`}
                        style={{ width: '120px' }}
                      />
                      <span>-</span>
                      <input
                        type="number"
                        value={parseRangeValue(constraintValue).max}
                        onChange={(e) => {
                          const { min } = parseRangeValue(constraintValue);
                          handleRangeValueChange(item.id, min, e.target.value);
                        }}
                        placeholder="Max"
                        className={`constraint-input ${isInvalid ? 'parameter-input-invalid' : ''}`}
                        style={{ width: '120px' }}
                      />
                    </div>
                  ) : (
                    <input
                      type="number"
                      value={constraintValue}
                      onChange={(e) => handleConstraintValueChange(item.id, e.target.value)}
                      placeholder="Value"
                      autoFocus={needsValueInput}
                      className={`constraint-input ${isInvalid ? 'parameter-input-invalid' : ''}`}
                    />
                  )
                )}
              </div>
            ) : (
              <span
                onClick={() => isEditable && !isLoading && setEditingConstraintId(item.id)}
                className={`editable-cell-value ${isEditable && !isLoading ? 'clickable' : 'non-clickable'}`}
                style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : (isEditable ? 'pointer' : 'default') }}
              >
                {isEditable ? getConstraintDisplayText() : '-'}
                {isEditable && !isLoading && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, alignSelf: 'center' }}>
                    <g clipPath="url(#clip0_440_1577)">
                      <path d="M1.43802 9.05782C1.46099 8.85107 1.47248 8.7477 1.50376 8.65108C1.53151 8.56535 1.57072 8.48378 1.62032 8.40856C1.67623 8.32377 1.74977 8.25023 1.89686 8.10314L8.50001 1.5C9.0523 0.947714 9.94773 0.947714 10.5 1.5C11.0523 2.05228 11.0523 2.94771 10.5 3.5L3.89686 10.1031C3.74977 10.2502 3.67623 10.3238 3.59144 10.3797C3.51622 10.4293 3.43465 10.4685 3.34892 10.4962C3.2523 10.5275 3.14893 10.539 2.94218 10.562L1.25 10.75L1.43802 9.05782Z" stroke="#103097" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_440_1577">
                        <rect width="12" height="12" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                )}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'validRange',
      header: 'Valid Range',
      cellClassName: 'non-editable-column',
      render: (item: any) => item.validRange,
    },
    {
      key: 'unit',
      header: 'Unit',
      cellClassName: 'non-editable-column',
      render: (item: any) => (item.unit),
    },
  ];

  const columns = selectedMode === 'design' ? designColumns : predictionColumns;

  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <button className="back-button" onClick={onBack} disabled={isLoading}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Configure {modelName} Parameters</h1>
        <button
          className="btn btn-link view-button"
          onClick={() => setIsDiagramModalOpen(true)}
        >
          <img src={eyeIcon} alt="View" width="20" height="20" />
          View Parameter Diagram
        </button>
        {onVisualize && (
          <button
            className="btn btn-link view-button"
            onClick={onVisualize}
            disabled={isLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"/>
              <line x1="12" y1="20" x2="12" y2="4"/>
              <line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
            Visualize
          </button>
        )}
      </div>

      {/* Mode Selection */}
      <div className="file-source-options">
        <div
          className={`file-source-card ${selectedMode === 'prediction' ? 'selected' : ''} ${isLoading ? 'disabled' : ''}`}
          onClick={() => !isLoading && setSelectedMode('prediction')}
          style={{ cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}
        >
          <div className="file-source-content">
            <div className="file-source-icon">
              <img src={forwardIcon} alt="Prediction" />
            </div>
            <div className="file-source-text">
            <h3>Prediction Model</h3>
            <p>Predict simulation outcomes from input parameters</p>
            </div>
            {selectedMode === 'prediction' && (
              <div className="checkmark-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                  <rect width="20.48" height="20.48" rx="10.24" fill="#29B829"/>
                  <path d="M6.40039 10.24L8.96039 12.8L14.0804 7.67999" stroke="white" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            )}
          </div>
        </div>

        <span className="mode-divider">or</span>

        <div
          className={`file-source-card ${selectedMode === 'design' ? 'selected' : ''} ${isLoading ? 'disabled' : ''}`}
          onClick={() => !isLoading && setSelectedMode('design')}
          style={{ cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}
        >
          <div className="file-source-content">
            <div className="file-source-icon">
              <img src={inverseIcon} alt="Design" />
            </div>
            <div className="file-source-text">
            <h3>Design Model</h3>
            <p>Find optimal parameters to achieve target performance</p>
            </div>
            {selectedMode === 'design' && (
              <div className="checkmark-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                  <rect width="20.48" height="20.48" rx="10.24" fill="#29B829"/>
                  <path d="M6.40039 10.24L8.96039 12.8L14.0804 7.67999" stroke="white" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="parameters-description">
        Configure the simulation parameters below. Default values are provided based on typical operating conditions. All parameters include validation ranges to ensure realistic simulation inputs.
      </p>

      {/* Parameters Table */}
      <div className="parameters-table-container">
        <Table
          items={parameters}
          columns={columns}
          scrollable={true}
          maxItemsPerPage={10}
        />
      </div>

      {/* Note */}
      <div className="parameters-note">
        <div className="note-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 14V10M10 6H10.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>
        <div className="note-text">
          <strong>Note:</strong> Parameter values are automatically validated against physical constraints. Invalid values will be highlighted and must be corrected before proceeding.
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '1rem', padding: '0.5rem' }}>
          {error}
        </div>
      )}

      {/* Validation message when values are out of range */}
      {hasInvalidValues && (
        <div className="validation-error-message" style={{ color: 'var(--error500)', marginBottom: '1rem', padding: '0.5rem', fontSize: '0.875rem' }}>
          Correct invalid values to be within the valid range before proceeding.
        </div>
      )}

      {/* Action Button */}
      <div className="wizard-actions">
        <button
          className="btn btn-primary continue-btn-full"
          onClick={selectedMode === 'prediction' ? handleGetPrediction : handleGetDesign}
          disabled={isLoading || hasInvalidValues}
        >
          {isLoading ? 'Loading...' : (selectedMode === 'prediction' ? 'Get Prediction' : 'Get Design')}
          {!isLoading && (
            <span className="arrow-right">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4.16699 10H15.8337M15.8337 10L10.0003 4.16669M15.8337 10L10.0003 15.8334" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          )}
        </button>
      </div>

      <ParameterDiagramModal
        isOpen={isDiagramModalOpen}
        onClose={() => setIsDiagramModalOpen(false)}
        imageUrl={diagramImageUrl}
      />
    </div>
  );
};

